import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "/logo.png";

export default function GuestLoginPage() {
  const navigate = useNavigate();
  const { guestLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await guestLogin(email, password);
      navigate('/portal');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Ugyldige loginoplysninger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="max-w-sm w-full animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Molin30" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl font-semibold text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Gæstelogin</h1>
          <p className="text-slate-400 text-sm">Brug din e-mail til at åbne gæsteportalen.</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-6" data-testid="login-error">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
            <p className="text-xs text-red-300/80 mt-2">
              Hvis du stadig ikke kan logge ind, så kontakt personalet ved check-in.
            </p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4" data-testid="guest-login-form">
          <div>
            <label className="text-sm text-slate-400 block mb-1.5">E-mail</label>
            <input
              type="email"
              placeholder="din@email.dk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none placeholder:text-slate-600"
              data-testid="guest-login-email"
              required
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1.5">Adgangskode</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-2.5 outline-none pr-10"
                data-testid="guest-login-password"
                required
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 rounded-md disabled:opacity-50" data-testid="guest-login-submit">
            {loading ? 'Logger ind...' : 'Log ind'}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-slate-500">
            Har du ikke en konto?{' '}
            <button onClick={() => navigate('/')} className="text-blue-400 hover:text-blue-300" data-testid="go-to-invite">Indtast din invitationskode</button>
          </p>
          <button onClick={() => navigate('/admin/login')} className="text-xs text-slate-600 hover:text-slate-400" data-testid="switch-to-admin-login">
            Admin-login
          </button>
        </div>
      </div>
    </div>
  );
}
